import { useReducer, useEffect, useMemo } from 'react'

import { PaymentCredits } from '@/types/Payment'
import { ChargingPoint } from '@/types/ChargingPoint'

import { StartChargingState, StartChargingStep } from './types'
import { startChargingReducer, createInitialState } from './reducer'
import { useStartChargingActions } from './useStartChargingActions'
import { GetPaymentCreditCardsResponse } from '@/api/payment/types'
import { numericUtils } from '@/utils/numeric'
import { tariffUtils } from '@/utils/tariff'
import { useToast } from '@/contexts/ToastContext'
import { HttpError } from '@/api/http'
import { useMutationPostChargingStart } from '@/api/charging/mutations'
import {
  ChargingSessionInAppNotificationPayload,
  isAwaitingExternalPayment,
} from '@/lib/in-app-notifications/categories/charging_session'
import { chargingUtils } from '@/utils/charging'
import { UseDisclosureReturn } from '@/hooks/useDisclosure'
import { useQueryChargingConfig } from '@/api/charging/queries'
import { hasSufficientWalletBalance } from '../../helpers/has-sufficient-wallet-balance'

const CARD_REREGISTRATION_ERROR_CODE = 'card_requires_reregistration'

export interface UseStartChargingProps {
  startChargingModal: UseDisclosureReturn
  chargingPoint: ChargingPoint | undefined
  paymentCredits: PaymentCredits | undefined
  creditCards: GetPaymentCreditCardsResponse | undefined
  notificationPayload?: ChargingSessionInAppNotificationPayload | null
}

export const useStartCharging = ({
  startChargingModal,
  chargingPoint,
  paymentCredits,
  creditCards,
  notificationPayload,
}: UseStartChargingProps) => {
  const open = startChargingModal.isOpen
  const { show } = useToast()

  const queryChargingConfig = useQueryChargingConfig()

  const balance = useMemo(() => {
    return paymentCredits?.balance ? numericUtils.parseBRLDecimalToNumber(Number(paymentCredits.balance)) : 0
  }, [paymentCredits])

  const allowFreeCharging = useMemo(() => chargingPoint?.is_free === true, [chargingPoint])

  const hasIdleRate = useMemo(
    () =>
      tariffUtils.hasChargeInTimeline(
        chargingPoint?.idle_timeline,
        chargingPoint?.current_idle_rate ?? chargingPoint?.idle_rate ?? 0
      ),
    [chargingPoint]
  )

  const pendingBalance = useMemo(() => {
    return balance < 0 ? Math.abs(balance) : 0
  }, [balance])

  const hasPositiveBalance = balance > 0
  const hasPendingBalance = pendingBalance > 0

  const activationFee = chargingPoint?.current_activation_fee ?? chargingPoint?.activation_fee ?? 0
  const minimumAmount = chargingUtils.getMinimumAmount(activationFee, pendingBalance)

  const initialStep = hasPendingBalance ? StartChargingStep.PENDING_BALANCE : StartChargingStep.AMOUNT

  const [state, dispatch] = useReducer(startChargingReducer, initialStep, createInitialState)

  const startChargingMutation = useMutationPostChargingStart({
    onError: (error: HttpError, payload) => {
      show(error.message || 'Ocorreu um erro ao iniciar o carregamento!', { severity: 'error' })

      if (error.code !== CARD_REREGISTRATION_ERROR_CODE) return

      if (payload?.card_id) {
        dispatch({ type: 'START_REREGISTRATION', payload: payload.card_id })
      } else {
        dispatch({ type: 'GO_TO_STEP', payload: StartChargingStep.ADD_CARD })
      }
    },
  })

  const shouldUseWalletBalanceOnly = (props: { useWalletBalance: boolean; amount: number | null; balance: number }) => {
    const { useWalletBalance, amount, balance } = props
    return Boolean(useWalletBalance) && hasSufficientWalletBalance(amount ?? 0, balance)
  }

  const isUsingWalletBalanceOnly = useMemo(() => {
    return shouldUseWalletBalanceOnly({
      useWalletBalance: state.useWalletBalance || false,
      amount: state.amount,
      balance,
    })
  }, [state, balance])

  const confirmPaidChargingStart = async (state: StartChargingState): Promise<void> => {
    if (
      state.amount === null ||
      state.amount === undefined ||
      !Number.isFinite(state.amount) ||
      state.amount < minimumAmount
    ) {
      show(
        `Informe pelo menos ${numericUtils.formatNumberToBRLCurrency(minimumAmount)} para cobrir a ativação e eventual saldo pendente.`,
        { severity: 'error' }
      )
      dispatch({ type: 'SET_PROCESSING', payload: false })
      return
    }

    await startChargingMutation.mutateAsync({
      charging_point_id: chargingPoint?.id as number,
      amount: chargingUtils.getSessionAmount(state.amount, pendingBalance),
      payment_method: state.paymentMethod || undefined,
      card_id: state.selectedCardId || undefined,
      use_wallet_balance: state.useWalletBalance ?? false,
    })

    dispatch({ type: 'GO_TO_STEP', payload: StartChargingStep.PROCESS_PAYMENT })
  }

  const confirmFreeChargingStart = async (): Promise<void> => {
    if (!chargingPoint?.id) return
    await startChargingMutation.mutateAsync({ charging_point_id: chargingPoint.id })
    show('Sessão gratuita iniciada com sucesso!', { severity: 'success' })
  }

  const onStartCharging = () => {
    if (allowFreeCharging) {
      confirmFreeChargingStart()
      return
    }
    startChargingModal.open()
  }

  const selectedCard = useMemo(() => {
    if (!state.selectedCardId) return undefined
    return creditCards?.results?.find((card) => card.id === state.selectedCardId)
  }, [state.selectedCardId, creditCards])

  const actions = useStartChargingActions({
    dispatch,
    state,
    hasPendingBalance,
    hasIdleRate,
    initialStep,
    onConfirm: confirmPaidChargingStart,
    onCancel: startChargingModal.close,
  })

  const isReturningToPayment = isAwaitingExternalPayment(notificationPayload)

  const pixData = useMemo(() => {
    const code = startChargingMutation.data?.code ?? notificationPayload?.payment_pix_data?.code
    const image = startChargingMutation.data?.image ?? notificationPayload?.payment_pix_data?.image
    if (!code || !image) return null

    return { code, image }
  }, [startChargingMutation.data, notificationPayload])

  useEffect(() => {
    if (!open) return

    if (isReturningToPayment) {
      actions.goToStep(StartChargingStep.PROCESS_PAYMENT)
    } else {
      actions.resetFlow()
    }
  }, [open, isReturningToPayment])

  return {
    state,
    balance,
    pendingBalance,
    hasPositiveBalance,
    hasPendingBalance,
    hasIdleRate,
    shouldUseWalletBalanceOnly,
    isUsingWalletBalanceOnly,
    chargingConfig: queryChargingConfig?.data,
    minimumAmount,
    selectedCard,
    chargingPoint,
    creditCards,
    pixData,
    onStartCharging,
    isStartingCharging: startChargingMutation.isPending,
    ...actions,
  }
}
